import { Controller } from "@hotwired/stimulus"
import {
  canUseOutbox,
  csrfTokenFromDocument,
  enqueueScheduleCreate,
  formDataToPayload,
  formHasAttachments,
  flushOutbox,
  registerOutboxBackgroundSync
} from "../pwa/outbox"
import { getIdentity } from "../pwa/snapshot"

// Trial/Meeting new フォーム専用。見た目は変えず submit を攔截する。
export default class extends Controller {
  static targets = ["requestId"]

  connect() {
    this.onSubmit = this.onSubmit.bind(this)
    this.element.addEventListener("submit", this.onSubmit, { capture: true })
    this.ensureRequestId()
  }

  disconnect() {
    this.element.removeEventListener("submit", this.onSubmit, { capture: true })
  }

  ensureRequestId() {
    const field = this.hasRequestIdTarget ? this.requestIdTarget : this.findOrCreateHiddenRequestId()
    if (!field.value) {
      field.value = crypto.randomUUID()
    }
  }

  findOrCreateHiddenRequestId() {
    let field = this.element.querySelector('input[name="client_request_id"]')
    if (!field) {
      field = document.createElement("input")
      field.type = "hidden"
      field.name = "client_request_id"
      this.element.prepend(field)
    }
    field.setAttribute("data-offline-submit-target", "requestId")
    return field
  }

  async onSubmit(event) {
    const form = this.element
    const formData = new FormData(form)
    const type = formData.get("schedule[type]") || formData.get("type")
    if (type !== "Trial" && type !== "Meeting") return

    // 繰り返し予定はオフライン作成の対象外。オンラインなら通常の HTML 送信へ素通しする
    const preset = formData.get("recurrence[preset]")
    if (preset && preset !== "none") {
      if (navigator.onLine) return

      event.preventDefault()
      event.stopPropagation()
      window.alert(this.offlineMessage("recurrence_requires_online"))
      return
    }

    const hasAttachments = formHasAttachments(formData)
    // 添付ありオンラインは legacy HTML / Turbo 経路へ素通し
    if (hasAttachments && navigator.onLine) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    this.ensureRequestId()
    const requestId = this.requestIdTarget.value
    formData.set("client_request_id", requestId)

    if (hasAttachments) {
      window.alert(this.offlineMessage("attachments_require_online"))
      return
    }

    const payload = formDataToPayload(formData)

    if (!navigator.onLine) {
      await this.enqueueOffline({ requestId, payload })
      return
    }

    try {
      const response = await fetch(form.action || "/schedules", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfTokenFromDocument() || ""
        },
        body: formData
      })

      if (response.status === 201 || response.status === 200) {
        const body = await response.json().catch(() => ({}))
        const url = scheduleSuccessUrl(body, response)
        if (window.Turbo?.visit) {
          window.Turbo.visit(url)
        } else {
          window.location.href = url
        }
        return
      }

      if ([401, 419].includes(response.status)) {
        window.alert(this.offlineMessage("auth_required"))
        return
      }

      if ([403, 404, 409, 422].includes(response.status)) {
        const body = await response.json().catch(() => ({}))
        const message = formatErrors(body) || this.offlineMessage("save_failed")
        window.alert(message)
        return
      }

      if (response.status >= 500) {
        await this.enqueueOffline({ requestId, payload })
        return
      }

      window.alert(this.offlineMessage("save_failed"))
    } catch (error) {
      this.reportError("online submit failed", error)
      await this.enqueueOffline({ requestId, payload, flushAfter: true })
    }
  }

  async enqueueOffline({ requestId, payload, flushAfter = false }) {
    let identity
    try {
      identity = await getIdentity()
    } catch (error) {
      this.reportError("identity lookup failed", error)
      window.alert(this.offlineMessage("save_failed"))
      return
    }

    try {
      await enqueueScheduleCreate({ requestId, payload, identity })
      await registerOutboxBackgroundSync(undefined, { identity })
      if (flushAfter) {
        flushOutbox().catch((flushError) => this.reportError("flush after enqueue failed", flushError))
      }
      window.alert(this.offlineMessage("queued"))
    } catch (error) {
      this.reportError("enqueue failed", error)
      window.alert(this.offlineMessage(canUseOutbox(identity) ? "save_failed" : "offline_not_enabled"))
    }
  }

  offlineMessage(key) {
    const messages = this.element.dataset.offlineSubmitMessagesValue
    if (messages) {
      try {
        const parsed = JSON.parse(messages)
        if (parsed[key]) return parsed[key]
      } catch (_error) {
        // fall through
      }
    }
    const defaults = {
      queued: "送信待ちとして保存しました。オンライン復帰後に自動送信します。",
      attachments_require_online: "添付ファイル付きの予定はオンライン接続後に保存してください。",
      auth_required: "再ログイン後に送信してください。",
      save_failed: "保存に失敗しました",
      offline_not_enabled: "この端末でオフライン保存を有効にしてから、送信待ちに保存できます。"
    }
    return defaults[key] || defaults.save_failed
  }

  reportError(context, error) {
    console.error(`[offline-submit] ${context}`, error)
  }
}

function formatErrors(body) {
  const errors = body?.errors
  if (!errors) return body?.error_code || null
  if (typeof errors === "string") return errors
  return Object.entries(errors)
    .map(([key, messages]) => `${key}: ${[].concat(messages).join(", ")}`)
    .join("\n")
}

// body.url の .json を踏まず、id / Location を優先して HTML path を返す。
export function scheduleSuccessUrl(body, response) {
  if (body?.id != null) return `/schedules/${body.id}`

  const location = response?.headers?.get?.("Location")
  if (location) {
    try {
      const parsed = new URL(location, window.location.origin)
      return `${parsed.pathname.replace(/\.json$/, "")}${parsed.search}`
    } catch (_error) {
      // fall through
    }
  }

  if (body?.url) {
    try {
      const parsed = new URL(String(body.url), window.location.origin)
      return `${parsed.pathname.replace(/\.json$/, "")}${parsed.search}`
    } catch (_error) {
      return String(body.url).replace(/\.json$/, "")
    }
  }

  return "/schedules"
}
